import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { QueryErrorResetBoundary, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CompaniesPage as CompaniesPageComponent } from './CompaniesPage.component';
import type {
  Company,
  InfiniteScrollProps,
  PageSize,
  PaginationProps,
  RefetchMode,
  SortOptionView,
} from './CompaniesPage.component';
import { NoAccessFallback } from './NoAccessFallback';
import { CompaniesErrorFallback, CompaniesLoadingFallback } from './CompaniesStates';
import {
  companiesInfiniteQueryOptions,
  companiesQueryOptions,
  CompaniesFetchError,
} from './companies-query';
import { meSquadsQueryOptions } from '@/shared/me-squads-query';
import type { CompaniesSearch } from './companies-search.schema';
import {
  applySortChange,
  applyToggleView,
  clearSortChange,
  deriveViewState,
  SORT_OPTIONS,
} from './companies-state';
import { useDebouncedValue, effectiveSearch } from '@eureca/ui/forms';
import { getKeycloak } from '@/auth/keycloak-instance';
import { getSelectedWorkspaceId } from '@/router/require-workspace';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';

/** Stable id for a sort option — `${sort}-${dir}`. */
function sortOptionId(sort: string, dir: string): string {
  return `${sort}-${dir}`;
}

/** Search debounce window — sweet spot for typeahead inputs in this repo. */
const SEARCH_DEBOUNCE_MS = 300;

function openSupportForm(): void {
  window.open('https://tally.so/r/w4OdxY', '_blank', 'noopener,noreferrer');
}

function logoutToLogin(): void {
  getKeycloak().logout({ redirectUri: `${window.location.origin}/login` });
}

interface ForbiddenBoundaryState {
  forbidden: boolean;
}

class ForbiddenBoundary extends Component<{ children: ReactNode }, ForbiddenBoundaryState> {
  state: ForbiddenBoundaryState = { forbidden: false };

  static getDerivedStateFromError(err: unknown): ForbiddenBoundaryState | null {
    if (err instanceof CompaniesFetchError && err.status === 403) {
      return { forbidden: true };
    }
    return null;
  }

  componentDidCatch(err: unknown): void {
    if (!(err instanceof CompaniesFetchError) || err.status !== 403) {
      throw err;
    }
  }

  render(): ReactNode {
    if (this.state.forbidden) {
      return <NoAccessFallback onContactSupport={openSupportForm} onLogout={logoutToLogin} />;
    }
    return this.props.children;
  }
}

class RetryableBoundary extends Component<
  { children: ReactNode; onReset: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(err: unknown): { error: Error } {
    return { error: err instanceof Error ? err : new Error(String(err)) };
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onReset();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <CompaniesErrorFallback
          onRetry={this.handleRetry}
          onLogout={logoutToLogin}
          message={this.state.error.message}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * Shared handler bundle that the cards-view and table-view inner components
 * both consume. Owning these in a single hook avoids duplication while keeping
 * each view free to use the data hook appropriate for its rendering mode.
 *
 * Also tracks `lastTrigger` so the inner component can render the right
 * refetch banner copy ("Buscando empresas…" vs "Reordenando empresas…")
 * while the silent refetch is in flight.
 */
function useSharedHandlers(search: CompaniesSearch) {
  const navigate = useNavigate();

  // FR-016 / FR-031 / FR-032 / FR-033 — debounce + min-chars + AbortSignal
  const [rawSearchInput, setRawSearchInput] = useState<string>(search.search ?? '');
  const debouncedRawInput = useDebouncedValue(rawSearchInput, SEARCH_DEBOUNCE_MS);
  const debouncedEffective = effectiveSearch(debouncedRawInput);

  // What kicked off the most recent URL change. Read by inner components to
  // pick the right inline-banner copy while data is refetching.
  const [lastTrigger, setLastTrigger] = useState<RefetchMode | undefined>(undefined);

  useEffect(() => {
    if (search.search === debouncedEffective) return;
    setLastTrigger('searching');
    void navigate({
      to: '/',
      search: (prev) => ({
        ...(prev as CompaniesSearch),
        search: debouncedEffective,
        page: 1,
      }),
    });
  }, [debouncedEffective, search.search, navigate]);

  function handleSearchChange(value: string) {
    setRawSearchInput(value);
  }
  function handleSelectSort(id: string) {
    const option = SORT_OPTIONS.find((o) => sortOptionId(o.sort, o.dir) === id);
    if (!option) return;
    setLastTrigger('sorting');
    void navigate({
      to: '/',
      search: (prev) => applySortChange(prev as CompaniesSearch, option),
    });
  }
  function handleClearSort() {
    setLastTrigger('sorting');
    void navigate({
      to: '/',
      search: (prev) => clearSortChange(prev as CompaniesSearch),
    });
  }
  function handleToggleView() {
    void navigate({
      to: '/',
      search: (prev) => applyToggleView(prev as CompaniesSearch),
    });
  }
  function handleCreateCompany() {
    void navigate({ to: '/companies/create' });
  }
  function handleOpenCompany(companyId: string) {
    void navigate({ to: '/programas', search: { companyId } });
  }
  function handleEditCompany(companyId: string) {
    void navigate({ to: '/companies/$companyId/edit', params: { companyId } });
  }
  function handlePageChange(page: number) {
    void navigate({
      to: '/',
      search: (prev) => ({ ...(prev as CompaniesSearch), page }),
    });
  }
  function handlePageSizeChange(pageSize: PageSize) {
    void navigate({
      to: '/',
      search: (prev) => ({ ...(prev as CompaniesSearch), pageSize, page: 1 }),
    });
  }

  const derived = deriveViewState(search);
  const sortOptions: SortOptionView[] = SORT_OPTIONS.map((o) => ({
    id: sortOptionId(o.sort, o.dir),
    label: o.label,
    isSelected: derived.selectedSortOption === o,
  }));

  return {
    rawSearchInput,
    derived,
    sortOptions,
    lastTrigger,
    clearLastTrigger: () => setLastTrigger(undefined),
    handlers: {
      handleSearchChange,
      handleSelectSort,
      handleClearSort,
      handleToggleView,
      handleCreateCompany,
      handleOpenCompany,
      handleEditCompany,
      handlePageChange,
      handlePageSizeChange,
    },
  };
}

/**
 * Cards-view inner component — uses `useInfiniteQuery` (NOT the Suspense
 * variant) so `placeholderData: keepPreviousData` actually kicks in. TanStack
 * Query 5's Suspense queries silently ignore `placeholderData` and re-suspend
 * on every queryKey change, which would surface the full skeleton instead of
 * the inline "Buscando empresas…" / "Reordenando empresas…" banner whenever
 * search/sort/pageSize changes.
 *
 * The cold-load fallback (no prior cache) is rendered explicitly by the
 * caller (`CompaniesPageContent`) when `data === undefined`.
 */
/** Multi-squad gate copy — kept in one place so create + edit pages stay in sync. */
const MULTI_SQUAD_GATE_TOOLTIP =
  'Você está vinculado a mais de um squad por isso não pode criar empresa. Contate um Administrador';

function CompaniesPageCardsContent({ search }: { search: CompaniesSearch }) {
  // ALL hooks must run unconditionally — early returns below are pure JSX.
  // Mixing hook calls with conditional returns triggers React's
  // "Rendered more hooks than during the previous render" error on the
  // first cache hit after a cold load.
  const query = useInfiniteQuery(companiesInfiniteQueryOptions(search));
  const meSquads = useQuery(meSquadsQueryOptions());
  const { rawSearchInput, derived, sortOptions, lastTrigger, clearLastTrigger, handlers } =
    useSharedHandlers(search);

  const isPageRefetching = query.isFetching && !query.isFetchingNextPage;
  const prevRefetchingRef = useRef(isPageRefetching);
  useEffect(() => {
    if (prevRefetchingRef.current && !isPageRefetching && lastTrigger) {
      clearLastTrigger();
    }
    prevRefetchingRef.current = isPageRefetching;
  }, [isPageRefetching, lastTrigger, clearLastTrigger]);

  if (query.error) throw query.error;
  if (!query.data) return <CompaniesLoadingFallback view="cards" />;

  // Defensive: drop any null/undefined pages or items. TanStack Query can
  // briefly hold a partial pages array under `keepPreviousData` overlap, and
  // we'd rather render an empty grid than crash with "Cannot read properties
  // of undefined (reading 'id')" inside CompaniesCardsView's .map.
  const companies: Company[] = query.data.pages
    .filter((p): p is NonNullable<typeof p> => p != null && Array.isArray(p.items))
    .flatMap((p) => p.items.filter((item): item is Company => item != null));
  const infinite: InfiniteScrollProps = {
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    onLoadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
  const refetchMode: RefetchMode | undefined =
    isPageRefetching && lastTrigger ? lastTrigger : undefined;
  // Server-reported total — read from the most recent page (each page response
  // carries the post-filter total). Lets the empty-state branching distinguish
  // "scope is empty" from "filter wiped the result set".
  const total = query.data.pages[0]?.total ?? 0;

  // Squad-count gate (EU-146): consultor in exactly 1 squad OR admin → can
  // create. Default to disabled while the squad query is loading; the BFF
  // call is JWT-projected so it returns in a few ms — a short flicker beats
  // letting the user start a flow that the backend would 422.
  const canCreate =
    meSquads.data !== undefined && (meSquads.data.isAdmin || meSquads.data.count === 1);

  return (
    <CompaniesPageComponent
      companies={companies}
      view="cards"
      total={total}
      page={search.page}
      searchValue={rawSearchInput}
      onSearchChange={handlers.handleSearchChange}
      hasActiveSearch={search.search !== undefined}
      sortOptions={sortOptions}
      selectedSortLabel={derived.selectedSortOption?.label}
      isSortSelected={derived.isSortSelected}
      onSelectSort={handlers.handleSelectSort}
      onClearSort={handlers.handleClearSort}
      isTableSelected={false}
      onToggleView={handlers.handleToggleView}
      onCreateCompany={handlers.handleCreateCompany}
      onOpenCompany={handlers.handleOpenCompany}
      onEditCompany={handlers.handleEditCompany}
      selectedCompanyId={getSelectedWorkspaceId()}
      titleBrand={
        getSelectedWorkspaceId() ? (
          <WorkspaceBrand companyId={getSelectedWorkspaceId() as string} />
        ) : undefined
      }
      onLogout={logoutToLogin}
      infinite={infinite}
      refetchMode={refetchMode}
      canCreate={canCreate}
      createDisabledTooltip={canCreate ? undefined : MULTI_SQUAD_GATE_TOOLTIP}
    />
  );
}

/**
 * Table-view inner component — single-page query. Pagination buttons update
 * the URL `page` param; pageSize change resets to page 1. Uses `useQuery`
 * (NOT the Suspense variant) so `placeholderData: keepPreviousData` keeps
 * the prior page visible while the next one fetches, surfacing the inline
 * "Buscando…/Reordenando empresas…" banner instead of the cold skeleton.
 */
function CompaniesPageTableContent({ search }: { search: CompaniesSearch }) {
  // ALL hooks must run unconditionally — see cards variant above.
  const query = useQuery(companiesQueryOptions(search));
  const meSquads = useQuery(meSquadsQueryOptions());
  const { rawSearchInput, derived, sortOptions, lastTrigger, clearLastTrigger, handlers } =
    useSharedHandlers(search);

  // Clear ONLY on the transition refetch=true → refetch=false. See cards
  // variant above for rationale (URL change is async and races setState).
  const prevTableRefetchingRef = useRef(query.isFetching);
  useEffect(() => {
    if (prevTableRefetchingRef.current && !query.isFetching && lastTrigger) {
      clearLastTrigger();
    }
    prevTableRefetchingRef.current = query.isFetching;
  }, [query.isFetching, lastTrigger, clearLastTrigger]);

  if (query.error) throw query.error;
  if (!query.data) return <CompaniesLoadingFallback view="table" />;

  const { data } = query;
  const refetchMode: RefetchMode | undefined =
    query.isFetching && lastTrigger ? lastTrigger : undefined;

  const pagination: PaginationProps = {
    page: data.page,
    pageSize: data.pageSize as PageSize,
    total: data.total,
    onPageChange: handlers.handlePageChange,
    onPageSizeChange: handlers.handlePageSizeChange,
  };

  const canCreate =
    meSquads.data !== undefined && (meSquads.data.isAdmin || meSquads.data.count === 1);

  // Same defensive filter as the cards path — guards against any null/
  // undefined entries in the BFF response that would crash the .map below.
  const tableCompanies = (data.items ?? []).filter(
    (item): item is NonNullable<(typeof data.items)[number]> => item != null
  );

  return (
    <CompaniesPageComponent
      companies={tableCompanies}
      view="table"
      total={data.total}
      page={data.page}
      searchValue={rawSearchInput}
      onSearchChange={handlers.handleSearchChange}
      hasActiveSearch={search.search !== undefined}
      sortOptions={sortOptions}
      selectedSortLabel={derived.selectedSortOption?.label}
      isSortSelected={derived.isSortSelected}
      onSelectSort={handlers.handleSelectSort}
      onClearSort={handlers.handleClearSort}
      isTableSelected={true}
      onToggleView={handlers.handleToggleView}
      onCreateCompany={handlers.handleCreateCompany}
      onOpenCompany={handlers.handleOpenCompany}
      onEditCompany={handlers.handleEditCompany}
      selectedCompanyId={getSelectedWorkspaceId()}
      titleBrand={
        getSelectedWorkspaceId() ? (
          <WorkspaceBrand companyId={getSelectedWorkspaceId() as string} />
        ) : undefined
      }
      onLogout={logoutToLogin}
      pagination={pagination}
      refetchMode={refetchMode}
      canCreate={canCreate}
      createDisabledTooltip={canCreate ? undefined : MULTI_SQUAD_GATE_TOOLTIP}
    />
  );
}

function CompaniesPageContent({ search }: { search: CompaniesSearch }) {
  return search.view === 'table' ? (
    <CompaniesPageTableContent search={search} />
  ) : (
    <CompaniesPageCardsContent search={search} />
  );
}

export default function CompaniesPage() {
  const search = useSearch({ from: '/_authenticated/' }) as CompaniesSearch;

  // No Suspense boundary here — the inner Cards/Table containers use plain
  // `useQuery`/`useInfiniteQuery` (with `placeholderData: keepPreviousData`)
  // and render the skeleton themselves only on cold load. That lets the
  // refetch banner show over prior data when search/sort/page changes.
  return (
    <ForbiddenBoundary>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <RetryableBoundary onReset={reset}>
            <CompaniesPageContent search={search} />
          </RetryableBoundary>
        )}
      </QueryErrorResetBoundary>
    </ForbiddenBoundary>
  );
}
